/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { ref } from 'ember-ref-bucket';

import { action } from '@ember/object';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';

import type { IResourceName } from 'volta/models/base-model';

const FILTER_FIELD_MIN_WIDTH = 150;

export interface BvFiltersSignature {
  Args: {
    onClearAll?: () => void;
    belowAppHeader?: boolean;
    moreFiltersButtonLabel?: string;
    isLoading?: boolean;
    focused?: boolean;
    disabled?: boolean;
    onSearchFocus?: (e: Event) => void;
    onSearchBlur?: (e: Event) => void;
    resourceName: IResourceName;
    queryPlaceholder?: string;
    hideTextSearch?: boolean;
    hideTags?: boolean;
    filterDefinitions: TFilterDefinition[];
    filterValues: TFilterValues;
    forceShowMoreFiltersButton?: boolean;
    onFiltersApply?: (filterValues: TFilterValues) => void;
    onFilterValueChange?: (filterValue?: TFilterValue) => void;
    onSearchChange?: (filterValues: TFilterValues, query: string) => Promise<unknown> | unknown;
    onClose?: () => void;
    searchValue?: string;
    searchKey?: string;
  };
  Blocks: {
    default: [{ toggleFilters: () => void }];
    tagsRight?: [{ toggleFilters: () => void }];
  };
  Element: HTMLDivElement;
}

export default class BvFiltersComponent extends Component<BvFiltersSignature> {
  @tracked availableWidth = 0;
  @tracked proxyButtonsWidth: Record<string, number> = {};
  @tracked isMoreFiltersContainerVisible = false;
  @tracked customFiltersOpen = false;

  searchOnInput = true;

  @ref('container') container?: HTMLElement;
  @ref('proxyButtonContainer') proxyButtonContainer?: HTMLElement;
  @ref('moreFiltersButtonContainerToggle') moreFiltersButtonContainerToggle?: HTMLElement;
  @ref('moreFiltersButtonContainer') moreFiltersButtonContainer?: HTMLElement;

  // Getters
  // ~~~~~

  @cached
  get definitionsDict() {
    const dict: Record<string, TFilterDefinition> = {};
    this.args.filterDefinitions.forEach((def) => (dict[def.queryParam ?? ''] = def));
    return dict;
  }

  get searchKey() {
    return this.args.searchKey ?? 'q';
  }

  get popoverableFilterDefinitions() {
    const { filterDefinitions } = this.args;
    return filterDefinitions ? filterDefinitions.filter((def) => def.isPrimary && !def.hidden) : [];
  }

  get filtersToRender() {
    const { availableWidth, proxyButtonsWidth, popoverableFilterDefinitions } = this;
    const filtersToReturn = [];
    let remainingWidth = availableWidth;
    for (let i = 0; remainingWidth > 0 && i < popoverableFilterDefinitions.length; i++) {
      const filter = popoverableFilterDefinitions[i]!;
      const actionWidth = proxyButtonsWidth[filter?.queryParam ?? ''];
      if (actionWidth && actionWidth <= remainingWidth) {
        filtersToReturn.push(filter);
        remainingWidth -= actionWidth;
      } else {
        // When we can't fit an action, we break the loop.
        // The ones that didn't fit will be accessible through the "More filters" button
        break;
      }
    }
    return filtersToReturn;
  }

  get enabledFilters() {
    const { filterValues } = this.args;
    const filters: Record<string, TFilterValue> = {};

    for (const field in filterValues) {
      if (Object.prototype.hasOwnProperty.call(filterValues, field) && field !== this.searchKey) {
        const filter = filterValues[field];
        if (filter && filter.enabled) {
          filters[field] = filter;
        }
      }
    }

    return filters;
  }

  get enabledFiltersCount() {
    return Object.keys(this.enabledFilters).length;
  }

  get shouldRenderMoreFiltersButton() {
    const { forceShowMoreFiltersButton, filterDefinitions } = this.args;
    const { filtersToRender } = this;
    const filtersWithoutSearch = filterDefinitions
      ? filterDefinitions.filter((f) => !f.hidden).length
      : 0;
    return (
      forceShowMoreFiltersButton ??
      (filtersToRender && filtersToRender.length !== filtersWithoutSearch)
    );
  }

  get customFilterDefinitions() {
    return (this.args.filterDefinitions ?? []).filter((f) => f.customFieldDefinition !== undefined);
  }

  // Actions
  // ~~~~~~

  @action
  handleResize() {
    this.measureProxyButtons(this.proxyButtonContainer);
    this.measureAvailableWidth();
  }

  @action
  handleProxyContainerInsert(container: HTMLElement) {
    this.measureProxyButtons(container);
    this.measureAvailableWidth();
  }

  @action
  handleDidInsert(_container: HTMLElement) {
    this.handleResize();
  }

  @action
  toggleFilters() {
    this.isMoreFiltersContainerVisible = !this.isMoreFiltersContainerVisible;
    if (!this.isMoreFiltersContainerVisible) {
      this.args.onClose?.();
    }
  }

  @action
  closeFilters() {
    this.isMoreFiltersContainerVisible = false;
    this.args.onClose?.();
  }

  @action
  filterValuesDidChange(value: TFilterValue, field: string) {
    const { filterValues, onFiltersApply, onFilterValueChange } = this.args;
    const updatedFilterValues = { ...filterValues };

    const filterValueForField: TFilterValue = {
      ...(updatedFilterValues?.[field] ?? ({} as TFilterValue)),
      ...value
    };
    updatedFilterValues[field] =
      filterValueForField.values.length > 0 ? filterValueForField : undefined;

    onFilterValueChange?.(filterValueForField);

    if (Object.keys(updatedFilterValues).length === 0) {
      // no filter added
      return false;
    }

    return onFiltersApply?.(updatedFilterValues);
  }

  // Helpers
  // ~~~~~~

  measureProxyButtons(proxyButtonContainer?: HTMLElement) {
    if (proxyButtonContainer) {
      const proxyButtonsWidth: Record<string, number> = {};
      // this number is magical, but tweaking it solved the problem of items overlapping
      const tolerance = 52;
      [...proxyButtonContainer.children].forEach((element) => {
        const buttonWidth = element.getBoundingClientRect().width + tolerance;
        // @ts-expect-error
        const buttonKey = element.dataset.id as string;
        if (buttonKey) {
          proxyButtonsWidth[buttonKey] = buttonWidth;
        }
      });

      this.proxyButtonsWidth = proxyButtonsWidth;
    }
  }

  measureAvailableWidth() {
    if (this.container && this.moreFiltersButtonContainer) {
      const containerWidth = this.container.getBoundingClientRect().width;
      const moreFiltersButtonWidth = this.moreFiltersButtonContainer.getBoundingClientRect().width;
      const filtersActionWidth = 0;
      this.availableWidth =
        containerWidth - FILTER_FIELD_MIN_WIDTH - moreFiltersButtonWidth - filtersActionWidth;
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    BvFilters: typeof BvFiltersComponent;
    'bv-filters': typeof BvFiltersComponent;
  }
}
